import React, { useContext, useEffect, useState, useMemo, useCallback, useLayoutEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { POST_CANCEL_ORDER } from 'services/api';
import Modal from 'components/Modal';

import { selectOrderById, getOrder, getStripeOrder } from 'redux/slices/order';

import Notification from 'components/Notification';
import Button from 'components/Button';
import { orderSuccess as TEXT } from 'locales/en';
import { useQueryParams, useMediaQuery } from 'hooks';
import { priceForAPI, priceFromAPI } from 'utils/price';
import { CURRENCY_SYMBOL } from 'utils/transform';
import { isNonFundraiserProduct as checkIsNotFundraiserProduct } from 'redux/helpers';

import breakpoints from 'utils/breakpoints';
import { BrandContext } from 'utils/brand-context';
import { getStoreColors } from './helpers';

import mc_logo from 'assets/icons/mc_symbol.svg';
import visa_logo from 'assets/icons/visa_blue.svg';
import amex_logo from 'assets/icons/amex_logo.svg';
import generic_card from 'assets/icons/card.svg';
import { FundraiserConfirmationScreen } from './components/ConfirmationScreen/FundraiserConfirmationScreen';
import { NonFundraiserConfirmationScreen } from './components/ConfirmationScreen/NonFundraiserConfirmationScreen';
import { showErrorMessage, showSuccessMessage } from 'redux/slices/notification';
import { getTerminalShippingMethods } from '../../redux/slices/javelinStore';

const OrderSuccess = () => {
  const brand = useContext(BrandContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const [cancelOrderModalOpen, setCancelModalOpen] = useState(false);
  const [isCancelLoading, setCancelLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const query = useQueryParams();
  const token = query.get('token');
  const checkoutUrl = useSelector((state) => state.checkout.url) || '/';
  const { stripeOrder } = useSelector(state => state.order);
  const isLarge = useMediaQuery(breakpoints.lg);
  const orderDetail = useSelector((state) => selectOrderById(state, id)) || {};
  const {
    orderProducts = [],
    store = { data: {} },
    payment_method,
    currency,
    subtotal,
    total,
    fees,
    charge,
    status,
    price_tax_excl,
    tax,
    shipping_cost
  } = orderDetail;

  const orderId = useMemo(() => {
    const _id = id.split('_');
    if (!_id.length) {
      return id;
    }
    return `Order ID ${_id[1]}`;
  }, [id]);

  const showCancelOrderButton = !['shipped', 'completed', 'rejected', 'cancelled'].includes(status?.toLowerCase());

  const checkIfIsNonFundraiser = useCallback((orders) => {
    return orders.map(pr => ({
      type: pr?.product?.data?.productable_type
    })).some(checkIsNotFundraiserProduct);
  }, [])

  const isNonFundraiserProduct = checkIfIsNonFundraiser(orderProducts);

  useEffect(() => {
    if (id && token) {
      dispatch(getOrder({ id, token })).then(({ type }) => {
        if (type.endsWith('rejected')) {
          history.replace(checkoutUrl);
        } else {
          setReady(true);
        }
      });

    } else {
      window.location.replace(process.env.REACT_APP_JAVELIN_ADMIN_URL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id && token && store?.data?.company?.data?.integration?.data?.type === 'stripe') {
      dispatch(getStripeOrder({ id, token }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, id, token]);

  const { terminalShippingMethods } = useSelector(
    (state) => state.javelinStore
  );

  useEffect(() => {
    if (terminalShippingMethods.length > 0) {
      return;
    }

    if (orderDetail && Object.keys(orderDetail).length > 0) {
      dispatch(
        getTerminalShippingMethods({
          storeId: orderDetail.store.data.id,
          country: orderDetail.country,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail])

  const productData = useMemo(() => {
    const data = orderProducts[0]?.product?.data || {};
    return {
      landing_page_url: data.landing_page_url,
      name: data.name,
      description: data.description,
      image: data.image,
    };
  }, [orderProducts]);

  const clientData = useMemo(() => {
    return store.data.client?.data || {};
  }, [store.data.client]);

  const renderCardLogo = useMemo(() => {
    let card_logo = generic_card;
    if (payment_method?.card_brand === 'mc') {
      card_logo = mc_logo;
    } else if (payment_method?.card_brand === 'amex') {
      card_logo = amex_logo;
    } else if (payment_method?.card_brand === 'visa') {
      card_logo = visa_logo;
    }

    return (
      <img
        className="w-9 lg:w-11 ml-0 mr-1 overflow-visible"
        src={card_logo}
        alt={payment_method?.card_brand}
      />
    );
  }, [payment_method?.card_brand]);


  const handleCancelHardwareOrder = useCallback(() => {
    setCancelLoading(true);
    POST_CANCEL_ORDER(orderDetail.id, { view_token: token })
      .then(() => {
        dispatch(showSuccessMessage('Order cancelled!'));
        setCancelModalOpen(false);
        dispatch(getOrder({ id, token }));
      }).catch(() => {
        dispatch(showErrorMessage("Some error occurred!"));
      })
      .finally(() => {
        setCancelLoading(false);
      })
  }, [dispatch, id, orderDetail.id, token])

  const cancelBtn = useMemo(() => {
    return (
      <div className='flex justify-end' >
        <Button
          disabled={isCancelLoading}
          title='Cancel Order'
          className='bg-javelin-danger-red mt-4 w-fit-content'
          onClick={() => setCancelModalOpen(true)}
        />
      </div>
    )
  }, [isCancelLoading]);

  useLayoutEffect(() => {
    if (!orderProducts.length) {
      return;
    }

    if (isNonFundraiserProduct) {
      document.title = 'Thank you for your order';
    } else {
      document.title = 'Thank you for your donation'
    }
  }, [isNonFundraiserProduct, orderProducts]);

  const shippingFees = isNonFundraiserProduct ? shipping_cost : priceForAPI(total) - price_tax_excl;
  const vatFee = priceForAPI(tax);
  const terminalTaxRate = isNonFundraiserProduct ? orderDetail.orderProducts[0]?.tax_rate : 0;

  const renderMobileSummary = useMemo(() => {
    return !isLarge && orderDetail.id ? (
      <>
        <div
          id="order-details"
          className="card h-auto flex flex-col rounded-none"
        >
          <div className="grid grid-cols-2 items-center gap-y-2">
            <h1
              data-testid="name"
              className="text-sm font-bold text-javelin-gray-dark truncate"
            >
              {store.data.name || '-'}
            </h1>
            <p
              data-testid="order_id"
              className="text-xs text-javelin-gray-dark text-right"
            >
              {orderId}
            </p>
          </div>
          <div className="mb-4">
            {orderProducts.map((item, idx) => (
              <div
                key={`${item.product_id}-${idx}`}
                className="grid grid-cols-2 items-center mb-1"
              >
                <p className="text-xs text-javelin-gray-light">
                  {item.quantity || 1} x {item.product?.data?.name || '-'}
                </p>
                <p className="text-xs text-javelin-gray-dark text-right text-opacity-60">
                  {CURRENCY_SYMBOL[item.currency || 'EUR']}
                  {priceFromAPI(isNonFundraiserProduct ? item.price_tax_excl : item.total)}
                </p>
              </div>
            ))}
          </div>
          <hr className="horizontal-line w-screen -mx-5 box-content" />
          <div className="grid grid-cols-2 items-center">
            <div className="flex flex-row items-center">
              {renderCardLogo}
              <p className="text-xs text-opacity-60 text-black h-full">
                ending {payment_method?.card_last4}
              </p>
            </div>
            <p className="text-sm font-bold text-javelin-gray-dark text-right">
              {CURRENCY_SYMBOL[currency || 'EUR']}
              {charge}
            </p>
          </div>
          {isNonFundraiserProduct && showCancelOrderButton && cancelBtn}
          {isNonFundraiserProduct && (
            <>
              <hr className='horizontal-line' />
              <p className='text-javelin-gray-dark text-opacity-30 my-4' >
                {store?.promotional_text}
              </p>
            </>
          )}
        </div>
        {!isNonFundraiserProduct && (
          <>
            <p className="p-5 text-xs text-black text-opacity-30">
              {TEXT.manyCauses}
            </p>
            <Button
              title={TEXT.browse}
              className="btn-blue w-11/12 px-0 py-4 block mx-auto mb-5 text-base"
              onClick={() => window.location.replace(clientData.homepage_url)}
            />
            <a
              className="p-5 text-xs text-black text-opacity-40 cursor-pointer"
              target="_blank"
              href={clientData.tos_url}
              rel="noreferrer"
            >
              {TEXT.terms}
            </a>
          </>
        )}
      </>
    ) : null;
  }, [
    isLarge,
    orderDetail.id,
    store,
    orderId,
    orderProducts,
    renderCardLogo,
    payment_method?.card_last4,
    currency,
    charge,
    isNonFundraiserProduct,
    showCancelOrderButton,
    cancelBtn,
    clientData.tos_url,
    clientData.homepage_url
  ]);

  const renderLargeViewSummary = useMemo(() => {
    return (
      <div
        data-testid="summary"
        className="bg-javelin-background min-h-screen pr-5 pl-10 xl:pr-14 2xl:pr-36 pt-20 w-auto col-span-6 xl:col-span-5"
      >
        {orderProducts.map((item, idx) => (
          <div
            key={`${item.product_id}-${idx}`}
            className="grid grid-cols-2 items-center mb-1"
          >
            <p className="text-xs lg:text-sm text-javelin-gray-light">
              {item.quantity || 1} x {item.product?.data?.name || '-'}
            </p>
            <p className="text-xs lg:text-sm text-javelin-gray-dark text-right text-opacity-60">
              {CURRENCY_SYMBOL[item.currency || 'EUR']}
              {priceFromAPI(isNonFundraiserProduct ? item.price_tax_excl : item.total)}
            </p>
          </div>
        ))}
        <hr className="horizontal-line my-8" />
        <div className="grid grid-cols-2 gap-y-4">
          <p className="text-sm text-javelin-gray-dark">{TEXT.subtotal}</p>
          <p className="text-sm text-javelin-gray-dark text-right font-bold">
            {CURRENCY_SYMBOL[currency || 'EUR']}
            {subtotal}
          </p>
          <p className="text-sm text-javelin-gray-dark">{TEXT.shipping}</p>
          <p className="text-sm text-javelin-gray-dark text-right font-bold">
            {CURRENCY_SYMBOL[currency || 'EUR']}
            {priceFromAPI(shippingFees)}
          </p>
          {!isNonFundraiserProduct && (
            <>
              <p className="text-sm text-javelin-gray-dark">{TEXT.fees}</p>
              <p className="text-sm text-javelin-gray-dark text-right font-bold">
                {CURRENCY_SYMBOL[currency || 'EUR']}
                {fees}
              </p>
            </>
          )}
          {isNonFundraiserProduct && (
            <>
              {
                stripeOrder && stripeOrder?.shipping?.address?.country === 'CA'
                  ? (
                    <>
                      {(stripeOrder?.total_tax_amounts || []).map((item) =>
                      (
                        <Fragment key={item.rate.display_name}>
                          <p className="text-sm text-javelin-gray-dark">{item?.rate?.display_name} {item?.rate?.percentage && '(' + item.rate.percentage + '%)'}</p>
                          <p className="text-sm text-javelin-gray-dark text-right font-bold">
                            {CURRENCY_SYMBOL[currency || 'EUR']}
                            {priceFromAPI(Number((item.rate.percentage / 100) * (orderDetail.price_tax_excl + orderDetail.shipping_cost)))}
                          </p>
                        </Fragment>
                      )
                      )}
                    </>
                  )
                  : (
                    <>
                      <p className="text-sm text-javelin-gray-dark">{TEXT.tax} {terminalTaxRate && '(' + terminalTaxRate + '%)'}</p>
                      <p className="text-sm text-javelin-gray-dark text-right font-bold">
                        {CURRENCY_SYMBOL[currency || 'EUR']}
                        {priceFromAPI(vatFee)}
                      </p>
                    </>
                  )
              }
            </>
          )}
        </div>
        <hr className="horizontal-line my-8" />
        <div data-testid="total" className="grid grid-cols-2 items-center">
          <div className="flex flex-row items-center">
            {renderCardLogo}
            <p className="text-sm text-opacity-60 text-black h-full">
              ending {payment_method?.card_last4}
            </p>
          </div>
          <p className="text-xl text-javelin-gray-dark text-right font-bold">
            {CURRENCY_SYMBOL[currency || 'EUR']}
            {charge}
          </p>
        </div>
        {isNonFundraiserProduct && showCancelOrderButton && cancelBtn}
        {isNonFundraiserProduct && (
          <>
            <hr className='horizontal-line' />
            <p className='text-javelin-gray-dark text-opacity-30 my-4' >
              {store?.promotional_text}
            </p>
          </>
        )}
      </div>
    );
  }, [
    orderProducts,
    currency,
    subtotal,
    shippingFees,
    isNonFundraiserProduct,
    fees,
    renderCardLogo,
    payment_method?.card_last4,
    charge,
    showCancelOrderButton,
    cancelBtn,
    vatFee,
    store,
    terminalTaxRate,
    stripeOrder,
    orderDetail?.price_tax_excl,
    orderDetail?.shipping_cost,
  ]);

  if (!ready) return null;

  return (
    <div className="flex lg:flex-col overflow-visible min-h-screen box-border w-full bg-javelin-background relative">
      <Notification className={`dashboard-notification fixed top-4 ${getStoreColors()}`} />
      <Modal
        isOpen={cancelOrderModalOpen}
        title="Are you sure you want to cancel?"
        onCancel={() => setCancelModalOpen(false)}
        onConfirm={handleCancelHardwareOrder}
      />
      {isNonFundraiserProduct ? (
        <NonFundraiserConfirmationScreen
          desktopSummary={renderLargeViewSummary}
          mobileSummary={renderMobileSummary}
          orderId={orderId}
          orderDetail={orderDetail}
          store={store}
        />
      ) : (
        <FundraiserConfirmationScreen
          brand={brand}
          clientData={clientData}
          desktopSummary={renderLargeViewSummary}
          mobileSummary={renderMobileSummary}
          orderId={orderId}
          productData={productData}
          productsCount={orderProducts.length}
          store={store}
          total={total}
        />
      )}
    </div>
  );
};

export default OrderSuccess;
